import { useEffect, useState } from 'react';
import { Button } from '@material-tailwind/react';
import { get } from 'lodash';
import { Breadcrumbs } from 'components/breadcrumbs';
import useWindowWidth from 'utills/InitWidthDevice';
import { useParse, useTranslation } from '@refinedev/core';
import ApiService from 'utills/ApiService';
import { DocumentIcon, PencilAltIcon, PhoneIcon, SparklesIcon, TagIcon, UserIcon } from '@heroicons/react/outline';
import { FullScreenLoading } from 'components/fullScreenLoading';
import toast from 'react-hot-toast';
import { isHaveActionPermission } from 'utills/helpers';

const AccountDetailForm = (props: any) => {
  const { translate, isMobile, token, accountID, language, permissions } = props;
  const [account, setAccount] = useState(null as any);
  const [loading, setLoading] = useState(false);
  const [fetchData, setFetchData] = useState(false);
  const [events, setEvents] = useState(null as any);

  useEffect(() => {
    if (accountID && accountID !== '') {
      const intervalFetchData = setInterval(() => {
        setFetchData(true);
        clearInterval(intervalFetchData);
      }, 100);
    }
  }, [accountID]);

  useEffect(() => {
    if (fetchData) {
      fetchAccountInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData]);

  const fetchAccountInfo = async () => {
    setLoading(true);
    try {
      const result = await ApiService.get(`account/${accountID}`, token);

      if (result) {
        setAccount(result);
      }
    } catch (error: any) {
      toast.error(error.message);
      setLoading(false);
    }

    setFetchData(false);
  }

  useEffect(() => {
    if (token !== '' && account && account.event_ids && account.event_ids.length > 0) {
      fetchEventList();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, token]);

  const fetchEventList = async () => {
    try {
      const result = await ApiService.get(`events`, token);

      if (result && result.length > 0) {
        const arr = [] as any;
        result.forEach((event: any) => {
          for (let i = 0; i < account.event_ids.length; i++) {
            if (account.event_ids[i] === event.id) {
              arr.push(get(event, `name_${language}`, ''));
            }
          }
        });

        setEvents([...arr]);
      }
    } catch (error: any) {
      toast.error(error.message);
    }

    setLoading(false);
  }

  const breadcrumbItems = [
    {
      label: translate('organization'),
      href: ''
    },
    {
      label: translate('account-management'),
      href: '/organization/account'
    },
    {
      label: translate('account-information'),
      href: ''
    },
  ];


  return (
    <div className="w-full rounded-[4px]">
      <Breadcrumbs type="wps" className="p-0" breadcrumbItems={breadcrumbItems} additionalLabel={(
        <>
          {isHaveActionPermission(
            "account",
            "organization",
            "edit",
            permissions
          ) && (
            <a href={`/organization/account/${accountID}/edit`}>
              <Button
                {...({} as any)}
                className="px-[16px] h-[42px] border border-brand-500 flex items-center justify-center text-14 font-500 text-brand-500 gap-[12px] bg-white"
              >
                <PencilAltIcon
                  className="size-[16px] text-brand-500"
                  strokeWidth={3}
                />
                {!isMobile && <span>{translate("btn-edit")}</span>}
              </Button>
            </a>
          )}
        </>
      )} />

      {
        account &&
        <div className="w-full bg-white p-[24px] mt-4 grid grid-cols-1">
          <div className={`${isMobile ? 'flex-col' : 'items-center'} w-full flex py-[16px] border-b border-[#c5ccd2]`}>
            <div className={`${isMobile ? 'w-full' : 'w-[30%] items-center'} flex gap-x-[12px]`}>
              <div className="size-[40px] rounded-full bg-[#feefe7] flex items-center justify-center">
                <TagIcon className="size-[20px] text-[#f1600d]" strokeWidth={3} />
              </div>

              <div className="form-item-title">{translate('status')}</div>
            </div>

            <div className={`${isMobile ? 'w-full pl-[52px]' : 'flex-1'} text-14`}>{(account.is_active) ? translate('active') : translate('in-active')}</div>
          </div>

          <div className={`${isMobile ? 'flex-col' : 'items-center'} w-full flex py-[16px] border-b border-[#c5ccd2]`}>
            <div className={`${isMobile ? 'w-full' : 'w-[30%] items-center'} flex gap-x-[12px]`}>
              <div className="size-[40px] rounded-full bg-[#feefe7] flex items-center justify-center">
                <img src="/images/icons/mail.svg" alt="icon-email" />
              </div>

              <div className="form-item-title">Email</div>
            </div>

            <div className={`${isMobile ? 'w-full pl-[52px]' : 'flex-1'} text-14`}>{account.email}</div>
          </div>

          <div className={`${isMobile ? 'flex-col' : 'items-center'} w-full flex py-[16px] border-b border-[#c5ccd2]`}>
            <div className={`${isMobile ? 'w-full' : 'w-[30%] items-center'} flex gap-x-[12px]`}>
              <div className="size-[40px] rounded-full bg-[#feefe7] flex items-center justify-center">
                <UserIcon className="size-[20px] text-[#f1600d]" strokeWidth={3} />
              </div>

              <div className="form-item-title">{translate('full-name')}</div>
            </div>

            <div className={`${isMobile ? 'w-full pl-[52px]' : 'flex-1'} text-14`}>{account.first_name} {account.last_name}</div>
          </div>

          <div className={`${isMobile ? 'flex-col' : 'items-center'} w-full flex py-[16px] border-b border-[#c5ccd2]`}>
            <div className={`${isMobile ? 'w-full' : 'w-[30%] items-center'} flex gap-x-[12px]`}>
              <div className="size-[40px] rounded-full bg-[#feefe7] flex items-center justify-center">
                <DocumentIcon className="size-[20px] text-[#f1600d]" strokeWidth={3} />
              </div>

              <div className="form-item-title">{translate('role-name')}</div>
            </div>

            <div className={`${isMobile ? 'w-full pl-[52px]' : 'flex-1'} text-14`}>{account.role_name}</div>
          </div>

          <div className={`${isMobile ? 'flex-col' : 'items-center'} w-full flex py-[16px] border-b border-[#c5ccd2]`}>
            <div className={`${isMobile ? 'w-full' : 'w-[30%] items-center'} flex gap-x-[12px]`}>
              <div className="size-[40px] rounded-full bg-[#feefe7] flex items-center justify-center">
                <PhoneIcon className="size-[20px] text-[#f1600d]" strokeWidth={3} />
              </div>

              <div className="form-item-title">{translate('contact-phone')}</div>
            </div>

            <div className={`${isMobile ? 'w-full pl-[52px]' : 'flex-1'} text-14`}>+{account.phone_area_code} {account.phone}</div>
          </div>

          <div className={`${isMobile ? 'flex-col' : 'items-center'} w-full flex py-[16px] border-b border-[#c5ccd2]`}>
            <div className={`${isMobile ? 'w-full' : 'w-[30%] items-center'} flex gap-x-[12px]`}>
              <div className="size-[40px] rounded-full bg-[#feefe7] flex items-center justify-center">
                <SparklesIcon className="size-[20px] text-[#f1600d]" strokeWidth={3} />
              </div>

              <div className="form-item-title">{translate('event')}</div>
            </div>

            {
              events && events.length > 0 &&
              <div className={`${isMobile ? 'w-full pl-[52px]' : 'flex-1'} text-14`}>
                {
                  events.map((item: any, index: number) => {
                    return (
                      <div key={`${item}_${index}`}>
                        {item}
                      </div>
                    )
                  })
                }
              </div>
            }
          </div>
        </div>
      }

      {
        loading &&
        <FullScreenLoading />
      }

    </div>
  )
}

const WPSAccountDetail = () => {
  const windowWidth = useWindowWidth();
  const [isMobile, setIsMobile] = useState(false);
  const { translate, getLocale } = useTranslation();
  const language = getLocale();
  const parse = useParse();
  const { params } = parse();
  const [accountID, setAccountID] = useState('');
  const [token, setToken] = useState('');
  const [permissions, setPermissions] = useState(null as any);

  useEffect(() => {
    const user = localStorage.getItem("USER");

    if (user) {
      const userData = JSON.parse(user);
      setToken(userData?.token);
    }
  }, []);

  useEffect(() => {
    if (params && params?.id && params?.id !== '') {
      setAccountID(params?.id);
    }
  }, [params]);

  useEffect(() => {
    if (windowWidth && windowWidth < 1024) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [windowWidth]);

  useEffect(() => {
    const user = localStorage.getItem("USER");

    if (user) {
      const userData = JSON.parse(user);

      if (userData?.type === 'wps') {
        const roles = [] as any;
        setToken(userData?.token);

        userData.permissions.forEach((item: any) => {
          const obj = {
            id: (item.resource !== "") ? item.resource : item.app,
            permissions: item.action,
            parrent: item.app
          };

          roles.push(obj);

          setPermissions(roles);

        });
      }
    }
  }, [windowWidth]);

  return (
    <div className="w-full">
      {
        (accountID !== '' && token !== '') &&
        <div className='w-full p-[16px]'>
          <AccountDetailForm
            isMobile={isMobile}
            translate={translate}
            accountID={accountID}
            token={token}
            language={language}
            permissions={permissions}
          />
        </div>
      }

    </div>
  )
};

export default WPSAccountDetail;